// Activity audit emitter: queues login/logout/page_view and flushes in
// batches. Identity is server-stamped from the JWT — we only send type/path.
// fetch keepalive (not sendBeacon) so the Authorization header survives
// page unload.

import { API_BASE } from '@/apiConfig';

type AuditType = 'login' | 'logout' | 'page_view';

interface QueuedEvent { type: AuditType; path: string; ts_ms: number }

let queue: QueuedEvent[] = [];
let tokenGetter: (() => string | null) | null = null;
let timer: number | null = null;

const FLUSH_MS = 10_000;
const FLUSH_AT = 20;

export function initAudit(getToken: () => string | null): void {
  tokenGetter = getToken;
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => void flush());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void flush();
    });
  }
}

export function recordAudit(type: AuditType, path: string = location.pathname): void {
  queue.push({ type, path, ts_ms: Date.now() });
  if (type !== 'page_view' || queue.length >= FLUSH_AT) {
    void flush();
    return;
  }
  if (timer == null) {
    timer = window.setTimeout(() => void flush(), FLUSH_MS);
  }
}

async function flush(): Promise<void> {
  if (timer != null) { clearTimeout(timer); timer = null; }
  const token = tokenGetter?.();
  if (!token || queue.length === 0) return;
  const events = queue.splice(0, FLUSH_AT * 2);
  try {
    await fetch(`${API_BASE}/audit`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ app: 'dashboard', events }),
    });
  } catch {
    // audit is best-effort; never disturb the app
  }
}
